<template>
  <div class="main-wrap odoo-blog-theme bg-light-gray">
    <main-header class="elevation-2" :to="'/bloger'" />

    <!-- Reading Progress Bar -->
    <div class="reading-progress-container">
      <div class="reading-progress-bar" :style="{ width: readingProgress + '%' }"></div>
    </div>

    <!-- Article Header -->
    <div class="blog-header pt-12 pt-md-16">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="11" lg="9" class="text-center pt-4 pt-md-8 px-4 px-sm-2">
            <div class="headline-shell rounded-xl px-4 px-sm-6 px-md-10 py-6 py-md-10">
            <!-- Breadcrumbs -->
            <v-breadcrumbs :items="breadcrumbs" class="justify-center pa-0 mb-6 mb-md-8 breadcrumb-custom">
              <template v-slot:divider>
                <v-icon size="16">{{ $vuetify.rtl ? 'mdi-chevron-left' : 'mdi-chevron-right' }}</v-icon>
              </template>
            </v-breadcrumbs>

            <!-- Title -->
            <h1 class="text-h4 text-sm-h3 text-md-h3 font-weight-black mb-4 mb-md-6 blog-title line-height-tight">
              {{ obj.name[locale] }}
            </h1>

            <p v-if="obj?.description?.[locale]" class="hero-excerpt text-body-1 text-sm-h6 mb-6 mb-md-8">
              {{ obj.description[locale] }}
            </p>

            <!-- Meta Information -->
            <div class="blog-meta d-flex justify-center align-center flex-wrap mb-4">
              <div class="meta-item d-flex align-center mx-2 mx-sm-3 mb-2">
                <v-icon size="20" class="me-2" color="primary">mdi-calendar-blank</v-icon>
                <span class="font-weight-medium">{{ formatDate(obj?.createdAt) }}</span>
              </div>
              <div class="meta-item d-flex align-center mx-2 mx-sm-3 mb-2">
                <v-icon size="20" class="me-2" color="primary">mdi-clock-outline</v-icon>
                <span class="font-weight-medium">{{ readingTime }} {{ $t('blog.minRead') || 'min read' }}</span>
              </div>
            </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Featured Image -->
    <v-container class="featured-image-container mb-8 mb-md-12 px-4 px-md-3">
      <v-row justify="center">
        <v-col cols="12" lg="11" class="pa-0 pa-sm-3">
          <v-img 
            :src="getImage(obj.img)" 
            :alt="obj.name[locale]" 
            class="rounded-xl elevation-5 hero-image mx-auto"
            max-height="600"
            cover
          ></v-img>
        </v-col>
      </v-row>
    </v-container>

    <!-- Content Section -->
    <v-container class="content-container px-4 px-sm-3">
      <v-row justify="center">
        <v-col cols="12" md="11" lg="9" class="pa-0 pa-sm-3">
          
          <v-card class="blog-content-wrapper rounded-xl pa-1 pa-md-12 elevation-0 border-thin bg-white mb-8 mb-md-10">
            <v-md-editor v-model="file" mode="preview"></v-md-editor>
          </v-card>

          <!-- Share Section -->
          <v-card class="share-section rounded-xl pa-6 pa-sm-8 border-thin elevation-0 bg-white text-center mb-8 mb-md-10">
            <h3 class="text-h6 text-sm-h5 font-weight-bold mb-4 mb-sm-6">
              {{ $t('blog.shareArticle') || 'Share this article' }}
            </h3>
            <div class="d-flex justify-center flex-wrap gap-2">
              <v-btn icon large color="#1877F2" class="share-btn mx-2" @click="shareOnFacebook" outlined>
                <v-icon>mdi-facebook</v-icon>
              </v-btn>
              <v-btn icon large color="#1DA1F2" class="share-btn mx-2" @click="shareOnTwitter" outlined>
                <v-icon>mdi-twitter</v-icon>
              </v-btn>
              <v-btn icon large color="#0A66C2" class="share-btn mx-2" @click="shareOnLinkedIn" outlined>
                <v-icon>mdi-linkedin</v-icon>
              </v-btn>
              <v-btn icon large color="grey darken-2" class="share-btn mx-2" @click="copyLink" outlined>
                <v-icon>mdi-link-variant</v-icon>
              </v-btn>
            </div>
          </v-card>

          <!-- Back to Blog Button -->
          <div class="text-center my-8 my-md-12">
            <v-btn :to="localePath('/bloger')" color="primary" large rounded class="font-weight-bold px-6 px-md-8 text-capitalize back-btn" elevation="2">
              <v-icon left>{{ $vuetify.rtl ? 'mdi-arrow-right' : 'mdi-arrow-left' }}</v-icon>
              {{ $t('blog.backToBlog') || 'Back to all articles' }}
            </v-btn>
          </div>
          
        </v-col>
      </v-row>
    </v-container>

    <div id="footer">
      <main-footer class="mt-6" />
    </div>

    <!-- Snackbar for copy link -->
    <v-snackbar v-model="snackbar" :timeout="3000" color="success" bottom>
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" class="font-weight-bold">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Scroll to Top Button -->
    <v-fab-transition>
      <v-btn v-show="showScrollTop" fab fixed bottom right color="primary" class="scroll-to-top-btn"
        @click="scrollToTop" elevation="6">
        <v-icon large>mdi-chevron-up</v-icon>
      </v-btn>
    </v-fab-transition>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/pages';

.odoo-blog-theme {
  --blog-primary: #3f51b5;
  --blog-secondary: #f44336;

  position: relative;
  overflow: hidden;
  .bg-light-gray { background-color: transparent !important; }
  .bg-white { background-color: #ffffff !important; }
  .border-thin { border: 1px solid #edf1f5 !important; }
  .line-height-tight { line-height: 1.15; letter-spacing: -0.5px; }
  .text-start { text-align: start !important; }

  .blog-header {
    position: relative;
    z-index: 2;
  }

  .headline-shell {
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.84));
    border: 1px solid rgba(63, 81, 181, 0.2);
    box-shadow: 0 18px 45px rgba(10, 35, 66, 0.08);
    backdrop-filter: blur(7px);
  }

  .blog-title {
    color: #0f2740;
    text-wrap: balance;
  }

  .hero-excerpt {
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
    color: #3f5467;
    line-height: 1.85;
  }

  .blog-meta {
    gap: 8px;
  }

  .meta-item {
    padding: 0.55rem 0.95rem;
    border-radius: 999px;
    background: #f5f6ff;
    border: 1px solid rgba(63, 81, 181, 0.2);
    color: #385063;
  }
  
  .reading-progress-container {
    position: fixed; top: 0; left: 0; width: 100%; height: 5px;
    background: transparent; z-index: 1000;
  }
  .reading-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #3f51b5, #f44336);
    transition: width 0.1s ease-out;
  }

  .breadcrumb-custom {
    ::v-deep .v-breadcrumbs__item {
      color: rgba(0, 0, 0, 0.6);
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-size: 0.75rem;
      &.v-breadcrumbs__item--disabled { color: var(--v-primary-base, #3f51b5); font-weight: 700; }
    }
  }

  .hero-image {
    border-radius: 20px !important;
    transition: transform 0.3s ease;
    border: 1px solid rgba(63, 81, 181, 0.15);
    background-color: #eee;
    box-shadow: 0 26px 60px rgba(63, 81, 181, 0.18) !important;
  }

  .blog-content-wrapper {
    position: relative;
    overflow: hidden;
    border-radius: 20px !important;
    box-shadow: 0 18px 48px rgba(16, 36, 58, 0.1);

    &::before {
      content: '';
      position: absolute;
      inset: 0 0 auto 0;
      height: 6px;
      background: linear-gradient(90deg, var(--blog-primary), var(--blog-secondary));
      z-index: 1;
    }

    ::v-deep .v-md-editor-preview {
      padding: 0;
      color: #2a3642;
      font-size: 1.15rem;
      line-height: 1.9;
      position: relative;
      z-index: 2;

      h1, h2, h3, h4, h5, h6 { color: #111827; font-weight: 800; margin-top: 2.5rem; margin-bottom: 1rem; }
      h1 { font-size: 2.25rem; border-bottom: 2px solid #edf1f5; padding-bottom: 0.5rem; }
      h2 { font-size: 1.875rem; }
      h3 { font-size: 1.5rem; }
      p { margin-bottom: 1.5rem; color: rgba(0, 0, 0, 0.85); }
      img { border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); margin: 2rem auto; max-width: 100%; display: block; }
      
      blockquote {
        background: #f4f9fc;
        padding: 1.5rem 1.5rem; margin: 2rem 0; font-style: italic; border-radius: 8px; color: #555;
        p { margin-bottom: 0; }
        
        [dir="ltr"] & { border-left: 4px solid var(--v-primary-base, #3f51b5); }
        [dir="rtl"] & { border-right: 4px solid var(--v-primary-base, #3f51b5); }
      }
      
      a {
        color: var(--blog-primary);
        text-decoration: none;
        font-weight: 600;
        background-image: linear-gradient(currentColor, currentColor);
        background-position: 0 100%;
        background-repeat: no-repeat;
        background-size: 0 2px;
        transition: background-size 0.2s ease;
        &:hover { background-size: 100% 2px; }
      }
      pre { background: #1e1e1e; border-radius: 12px; padding: 1.5rem; color: #fff; margin: 2.5rem 0; overflow-x: auto; }
      code { background: rgba(0,0,0,0.04); color: #e83e8c; padding: 0.2em 0.4em; border-radius: 6px; font-size: 0.9em; }
      pre code { background: transparent; color: inherit; padding: 0; }
      ul, ol { padding-left: 1.5rem; margin-bottom: 1.5rem; li { margin-bottom: 0.5rem; } }
    }
  }

  .share-section {
    background: linear-gradient(180deg, #ffffff 0%, #f7fbfe 100%) !important;
    border-radius: 20px !important;
    box-shadow: 0 14px 32px rgba(63, 81, 181, 0.12);

    .share-btn {
      transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
      background: #ffffff !important;
      border-width: 2px;
      border-radius: 14px;
      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 20px rgba(0,0,0,0.08);
      }
    }
  }

  .back-btn {
    letter-spacing: 0.2px;
    box-shadow: 0 12px 24px rgba(63, 81, 181, 0.24) !important;
  }

  .scroll-to-top-btn {
    bottom: 40px !important; right: 40px !important; z-index: 999; border-radius: 50%;
    &:hover { transform: translateY(-6px); }
  }
}

@media (max-width: 960px) {
  .odoo-blog-theme {
    .headline-shell {
      padding: 1.5rem 1.25rem !important;
    }

    .hero-excerpt {
      line-height: 1.75;
    }

    .share-section {
      padding: 1.25rem !important;
    }

    .share-btn {
      width: 44px;
      height: 44px;
    }

    .blog-content-wrapper ::v-deep .v-md-editor-preview {
      font-size: 1.05rem;
      h1 { font-size: 2rem; }
      h2 { font-size: 1.6rem; }
      h3 { font-size: 1.35rem; }
      img { margin: 1.5rem auto; }
      blockquote { padding: 1rem; }
    }
  }
}
@media (max-width: 600px) {
  .odoo-blog-theme {
    .breadcrumb-custom {
      margin-bottom: 1rem !important;
    }

    .breadcrumb-custom ::v-deep .v-breadcrumbs {
      flex-wrap: wrap;
      row-gap: 0.25rem;
      justify-content: center;
    }

    .scroll-to-top-btn { bottom: 20px !important; right: 20px !important; }
    .line-height-tight { line-height: 1.25; }
    .hero-image { border-radius: 12px !important; }
    .headline-shell { padding: 1.1rem 1rem !important; }
    .hero-excerpt { font-size: 0.95rem; line-height: 1.7; }
    .meta-item { padding: 0.45rem 0.7rem; font-size: 0.85rem; }

    .share-section {
      padding: 1rem !important;
    }

    .share-btn {
      width: 40px;
      height: 40px;
      margin: 0.2rem !important;
    }

    .back-btn {
      width: 100%;
      max-width: 340px;
    }
    
    .blog-content-wrapper ::v-deep .v-md-editor-preview {
      font-size: 1rem;
      h1 { font-size: 1.75rem; margin-top: 1.5rem; }
      h2 { font-size: 1.4rem; margin-top: 1.5rem; }
      p { margin-bottom: 1rem; line-height: 1.7; }
      pre { padding: 1rem; margin: 1.5rem 0; font-size: 0.85em; border-radius: 8px; }
      blockquote { margin: 1.5rem 0; font-size: 0.95em; }
    }
  }
}

@media (max-width: 420px) {
  .odoo-blog-theme {
    .blog-header {
      padding-top: 2rem !important;
    }

    .blog-title {
      font-size: 1.55rem !important;
    }

    .hero-excerpt {
      font-size: 0.9rem;
      margin-bottom: 1rem !important;
    }

    .meta-item {
      font-size: 0.8rem;
      padding: 0.35rem 0.6rem;
    }

    .blog-content-wrapper ::v-deep .v-md-editor-preview {
      h1 { font-size: 1.55rem; }
      h2 { font-size: 1.25rem; }
      pre { font-size: 0.78em; }
    }
  }
}
</style>

<script>
import BlogHeader from '~/components/Header/BlogHeader'
import Footer from '~/components/Footer'
import {
  buildSeoMeta,
  buildArticleSchema,
  buildStructuredDataScripts,
} from '~/utils/seo'

export default {
  components: {
    'main-header': BlogHeader,
    'main-footer': Footer,
  },
  data() {
    return {
      snackbar: false,
      snackbarText: '',
      readingProgress: 0,
      showScrollTop: false,
    }
  },
  mounted() {
    // Add scroll listener for reading progress
    if (process.client) {
      window.addEventListener('scroll', this.updateReadingProgress);
      // Initial calculation
      this.updateReadingProgress();
    }
  },
  beforeDestroy() {
    // Clean up scroll listener
    if (process.client) {
      window.removeEventListener('scroll', this.updateReadingProgress);
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    breadcrumbs() {
      return [
        {
          text: this.$t('header.home') || 'Home',
          disabled: false,
          href: this.localePath('/'),
          exact: true
        },
        {
          text: this.$t('header.blog') || 'Blog',
          disabled: false,
          href: this.localePath('/bloger'),
          exact: true
        },
        {
          text: this.obj.name[this.locale],
          disabled: true,
        }
      ]
    },
    readingTime() {
      // Calculate reading time based on content
      // Average reading speed: 200 words per minute
      if (!this.file) return 5;

      const words = this.file.trim().split(/\s+/).length;
      const minutes = Math.ceil(words / 200);
      return minutes;
    },
    currentUrl() {
      if (process.client) {
        return window.location.href;
      }
      return `https://app.motanamy.com${this.$route.path}`;
    }
  },
  async asyncData({ $axios, params, error, i18n }) {
    try {
      return { ... (await $axios.get(`/bloger/${params.id}?lang=${i18n.locale}`)).data }
    } catch (err) {
      return error({ statusCode: 404, message: 'Post Not Found' })
    }
  },
  methods: {
    updateReadingProgress() {
      if (!process.client) return;

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // Calculate the percentage scrolled
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Clamp between 0 and 100
      this.readingProgress = Math.min(100, Math.max(0, scrollPercentage));

      // Show scroll to top button after scrolling 300px
      this.showScrollTop = scrollTop > 300;
    },
    scrollToTop() {
      if (!process.client) return;

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    getImage(path) {
      if (!path) return '/images/default-blog.jpg';
      return `${this.geteRelativeUrl()}file/assets?path=${path}`;
    },
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };

      // Use provided date or default to current date
      const dateToFormat = date ? new Date(date) : new Date();

      return dateToFormat.toLocaleDateString(this.locale, options);
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.currentUrl)}`;
      this.openShareWindow(url);
    },
    shareOnTwitter() {
      const text = this.obj.name[this.locale];
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(this.currentUrl)}`;
      this.openShareWindow(url);
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.currentUrl)}`;
      this.openShareWindow(url);
    },
    openShareWindow(url) {
      if (process.client) {
        window.open(url, 'share-dialog', 'width=600,height=450');
      }
    },
    async copyLink() {
      if (process.client && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(this.currentUrl);
          this.snackbarText = this.$t('blog.linkCopied') || 'Link copied to clipboard!';
          this.snackbar = true;
        } catch (err) {
          console.error('Failed to copy:', err);
          this.snackbarText = this.$t('blog.copyFailed') || 'Failed to copy link';
          this.snackbar = true;
        }
      }
    }
  },
  head() {
    const encodedImagePath = encodeURIComponent(this.obj.img);
    const title = this.$t("brand.name") + ' - ' + this.obj.name[this.locale];
    const description = this.obj.description[this.locale];
    const image = `https://app.motanamy.com/api/file/assets?path=${encodedImagePath}`;
    const articleSchema = buildArticleSchema({
      title: this.obj.name[this.locale],
      description,
      path: this.$route.path,
      image,
      datePublished: this.obj.createdAt,
      dateModified: this.obj.updatedAt || this.obj.createdAt,
      inLanguage: this.locale,
    });

    return {
      title,
      meta: buildSeoMeta({
        title,
        description,
        path: this.$route.path,
        image,
        type: 'article',
      }),
      __dangerouslyDisableSanitizers: ['script'],
      script: buildStructuredDataScripts([articleSchema]),
    }
  }
}
</script>
